import type { UseFetchConfigReturn } from './index';

const QUERY_BOOTSTRAP_PREPARED_TRANSITION_LEASE_MS = 10_000;

/**
 * Matches the cloud-scoped identity of the active `useRovoConfig` SPA cache. Product changes cross
 * a runtime boundary, while account invalidation belongs to that source cache rather than this
 * short-lived in-memory handoff.
 */
export type QueryBootstrapSnapshotIdentity = Readonly<{
	cloudId: string;
}>;

export type QueryBootstrapSnapshotConfig = Readonly<Omit<UseFetchConfigReturn, 'onOAuthSuccess'>>;

export type QueryBootstrapSnapshot = Readonly<{
	identity: QueryBootstrapSnapshotIdentity;
	config: QueryBootstrapSnapshotConfig;
}>;

export type PreparedQueryBootstrapSnapshotTransition = Readonly<{
	snapshot: QueryBootstrapSnapshot;
	expiresAt: number;
}>;

let currentSnapshot: QueryBootstrapSnapshot | null = null;
let preparedTransition: PreparedQueryBootstrapSnapshotTransition | null = null;

export const createQueryBootstrapSnapshotIdentity = ({
	cloudId,
}: {
	cloudId: string | undefined;
}): QueryBootstrapSnapshotIdentity | null => {
	if (!cloudId) {
		return null;
	}

	return Object.freeze({ cloudId });
};

const identitiesMatch = (
	left: QueryBootstrapSnapshotIdentity,
	right: QueryBootstrapSnapshotIdentity,
): boolean => left.cloudId === right.cloudId;

export const isQueryBootstrapSnapshotValidForIdentity = (
	snapshot: QueryBootstrapSnapshot,
	identity: QueryBootstrapSnapshotIdentity,
): boolean => identitiesMatch(snapshot.identity, identity);

/**
 * Publishes one complete config snapshot. The store intentionally retains only the latest site
 * identity so tenant changes cannot fall back to an older in-tab snapshot.
 */
export const publishQueryBootstrapSnapshot = (args: {
	identity: QueryBootstrapSnapshotIdentity;
	config: UseFetchConfigReturn;
}): QueryBootstrapSnapshot | null => {
	const { identity, config } = args;

	if (!config.isConfigLoaded) {
		return null;
	}

	const { onOAuthSuccess: _onOAuthSuccess, ...configWithoutCallbacks } = config;
	const snapshot = Object.freeze({
		identity,
		config: Object.freeze(configWithoutCallbacks),
	}) satisfies QueryBootstrapSnapshot;

	currentSnapshot = snapshot;
	return snapshot;
};

export const readQueryBootstrapSnapshot = (
	identity: QueryBootstrapSnapshotIdentity,
): QueryBootstrapSnapshot | null => {
	if (
		currentSnapshot === null ||
		!isQueryBootstrapSnapshotValidForIdentity(currentSnapshot, identity)
	) {
		return null;
	}

	return currentSnapshot;
};

/**
 * Marks the current matching snapshot as eligible for the imminent Quick Find → Search Page
 * transition. Snapshot publication alone is not sufficient: direct navigation must retain the
 * existing asynchronous bootstrap path.
 */
export const prepareQueryBootstrapSnapshotForTransition = (
	identity: QueryBootstrapSnapshotIdentity,
	now: number = Date.now(),
): boolean => {
	const snapshot = readQueryBootstrapSnapshot(identity);
	preparedTransition = snapshot
		? Object.freeze({
				snapshot,
				expiresAt: now + QUERY_BOOTSTRAP_PREPARED_TRANSITION_LEASE_MS,
			})
		: null;
	return snapshot !== null;
};

export const readPreparedQueryBootstrapSnapshot = (
	identity: QueryBootstrapSnapshotIdentity,
	now: number = Date.now(),
): PreparedQueryBootstrapSnapshotTransition | null => {
	if (
		!preparedTransition ||
		preparedTransition.expiresAt <= now ||
		!isQueryBootstrapSnapshotValidForIdentity(preparedTransition.snapshot, identity)
	) {
		return null;
	}

	return preparedTransition;
};

/**
 * Completes a prepared handoff without discarding the reusable snapshot. Comparing the unique
 * preparation handle prevents an older route commit from clearing a newer preparation, including
 * when both transitions prepared the same snapshot.
 */
export const completeQueryBootstrapSnapshotTransition = (
	transitionToComplete: PreparedQueryBootstrapSnapshotTransition,
): void => {
	if (preparedTransition === transitionToComplete) {
		preparedTransition = null;
	}
};

export const clearQueryBootstrapSnapshot = (): void => {
	currentSnapshot = null;
	preparedTransition = null;
};
